import { useRef, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Draggable before/after reveal. Bare shell (before) on left, finished (after) on right.
export default function BeforeAfter({ before, after, labelBefore = "Bare shell", labelAfter = "Delivered", testid }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [interacted, setInteracted] = useState(false);
  const dragging = useRef(false);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      update(x);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [update]);

  // Auto-demo nudge when it first scrolls into view, so users see it's draggable.
  useEffect(() => {
    if (!inView || interacted) return;
    let raf;
    const start = performance.now();
    const dur = 2000;
    const tick = (t) => {
      if (dragging.current || interacted) return;
      const p = Math.min(1, (t - start) / dur);
      // ease in-out demo sweep: 50 -> 72 -> 28 -> 50
      const wave = Math.sin(p * Math.PI * 2) * 22;
      setPos(50 + wave);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setPos(50);
    };
    const timer = setTimeout(() => { raf = requestAnimationFrame(tick); }, 500);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [inView, interacted]);

  const start = (e) => {
    dragging.current = true;
    setInteracted(true);
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    update(x);
  };

  return (
    <div
      ref={ref}
      data-testid={testid}
      data-cursor="hover"
      className="group relative aspect-[4/3] w-full cursor-ew-resize select-none overflow-hidden rounded-sm bg-navy-800"
      onMouseDown={start}
      onTouchStart={start}
    >
      {/* After (full, underneath) */}
      <img src={after} alt="Finished interior" draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      <span className="absolute right-4 top-4 z-10 rounded-full bg-navy-900/70 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur">
        {labelAfter}
      </span>

      {/* Before (clipped from left) */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt="Bare shell" draggable={false} className="h-full w-full object-cover" />
        <span className="absolute left-4 top-4 rounded-full bg-navy-900/70 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-offwhite/80 backdrop-blur">
          {labelBefore}
        </span>
        <div className="absolute inset-0 bg-navy-900/30" />
      </div>

      {/* Handle */}
      <div className="absolute top-0 bottom-0 z-20" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="h-full w-[2px] bg-gold shadow-[0_0_20px_rgba(212,175,55,0.6)]" />
        <motion.div
          className="absolute top-1/2 left-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gold bg-navy-900/85 text-gold backdrop-blur"
          animate={interacted ? { scale: 1 } : { scale: [1, 1.12, 1] }}
          transition={interacted ? {} : { repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
        >
          <ChevronLeft size={16} strokeWidth={2} className="-mr-1" />
          <ChevronRight size={16} strokeWidth={2} className="-ml-1" />
        </motion.div>
      </div>

      {/* Persistent hint until first interaction */}
      <AnimatePresence>
        {!interacted && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="pointer-events-none absolute bottom-4 left-1/2 z-30 -translate-x-1/2"
          >
            <motion.span
              animate={{ opacity: [0.55, 1, 0.55] }}
              transition={{ repeat: Infinity, duration: 1.8 }}
              className="flex items-center gap-2 rounded-full border border-gold/40 bg-navy-900/80 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-gold backdrop-blur"
            >
              <ChevronLeft size={12} /> Drag to reveal <ChevronRight size={12} />
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
